import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import {
  FileText,
  ArrowRight,
  Loader2,
  Building2,
  ShoppingBag,
  ShieldCheck,
  Store,
  Network,
  Truck,
  Globe,
  ChevronDown,
  Save,
  CircleCheckBig,
  Building,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

// Ã¢â€â‚¬Ã¢â€â‚¬ Banking / Financial domain
const BANKING_REGULATORS = [
  "RBI",
  "SEBI",
  "IRDAI",
  "CBUAE",
  "DFSA",
  "ADGM",
  "FCA",
  "OCC",
  "FDIC",
  "ECB",
  "MAS",
  "HKMA",
  "OJK",
  "FSA",
];
const BANKING_REGULATORS_BY_COUNTRY = {
  india: ["RBI", "SEBI", "IRDAI", "FIU-IND"],
  uae: ["CBUAE", "DFSA", "ADGM", "SCA", "UAE FIU"],
  "united arab emirates": ["CBUAE", "DFSA", "ADGM", "SCA", "UAE FIU"],
  usa: ["Federal Reserve", "OCC", "FDIC", "SEC", "FINRA", "FinCEN"],
  "united states": ["Federal Reserve", "OCC", "FDIC", "SEC", "FINRA", "FinCEN"],
  uk: ["FCA", "PRA", "Bank of England"],
  "united kingdom": ["FCA", "PRA", "Bank of England"],
  eu: ["ECB", "EBA", "ESMA", "EIOPA"],
  singapore: ["MAS"],
  "hong kong": ["HKMA", "SFC", "IA"],
  indonesia: ["OJK", "Bank Indonesia", "PPATK"],
};
const BANKING_AUDIT_TYPES = ["Regulatory", "Process", "IT", "Risk-based", "Thematic", "Credit Risk", "AML/CFT", "Inventory Reconciliation", "Interest Recalculation & Slab Compliance Audit"];
const BANKING_DEPARTMENTS = [
  "Retail Banking",
  "Corporate Banking",
  "Treasury",
  "Operations",
  "Compliance",
  "Risk Management",
  "Internal Audit",
  "IT & Information Security",
];

// Ã¢â€â‚¬Ã¢â€â‚¬ Platform / E-Commerce domain
const PLATFORM_REGULATORS = ["FSSAI", "GST Council", "DPDP", "MCA21", "TRAI", "IT Act", "Consumer Protection Act"];
const PLATFORM_REGULATORS_BY_COUNTRY = {
  india: ["DPDP", "GST Council", "FSSAI", "Consumer Protection Act", "IT Act", "MCA21", "TRAI"],
  uae: ["UAE PDPL", "TDRA", "MOEC Consumer Protection", "FTA VAT", "DED"],
  "united arab emirates": ["UAE PDPL", "TDRA", "MOEC Consumer Protection", "FTA VAT", "DED"],
  usa: ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code"],
  "united states": ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code"],
  uk: ["ICO", "CMA", "FCA", "PCI-DSS"],
  "united kingdom": ["ICO", "CMA", "FCA", "PCI-DSS"],
  eu: ["GDPR", "DSA", "DMA", "PSD2", "PCI-DSS"],
  singapore: ["PDPC", "CCCS", "IRAS GST", "IMDA"],
  indonesia: ["PDP Law", "Kominfo", "BPKN", "DGT VAT"],
};
const PLATFORM_AUDIT_TYPES = ["Platform Integrity", "Partner Ecosystem", "Technology Governance", "Fraud & Trust", "Financial & Marketplace Controls", "Operational Platform Controls"];
const PLATFORM_DEPARTMENTS = [
  "Order Processing", "Delivery Fleet Operations", "Restaurant Network",
  "Coupon & Promotions", "Payout & Finance", "IT Operations & Security", "Customer Trust & Safety"
];

// Ã¢â€â‚¬Ã¢â€â‚¬ Insurance domain
const INSURANCE_REGULATORS = ["OJK", "FSA", "IRDAI", "LIC", "GIC", "Lloyd's", "EIOPA", "NAIC", "FCA", "PRA", "SAMA"];
const INSURANCE_REGULATORS_BY_COUNTRY = {
  india: ["IRDAI", "RBI", "SEBI"],
  uae: ["CBUAE", "DFSA", "ADGM FSRA", "Insurance Authority"],
  "united arab emirates": ["CBUAE", "DFSA", "ADGM FSRA", "Insurance Authority"],
  usa: ["NAIC", "State Insurance Departments", "FINRA", "SEC"],
  "united states": ["NAIC", "State Insurance Departments", "FINRA", "SEC"],
  uk: ["FCA", "PRA", "Lloyd's"],
  "united kingdom": ["FCA", "PRA", "Lloyd's"],
  eu: ["EIOPA", "Solvency II", "GDPR"],
  singapore: ["MAS"],
  indonesia: ["OJK"],
};
const INSURANCE_AUDIT_TYPES = ["Claims Audit", "Underwriting Audit", "IT Governance"];
const INSURANCE_DEPARTMENTS = ["Claims", "Underwriting","IT"];

// Ã¢â€â‚¬Ã¢â€â‚¬ Retail & Consumer domain (stores, omnichannel, malls, adjacent verticals)
const RETAIL_REGULATORS = [
  "DPDP",
  "GST Council",
  "Consumer Protection Act",
  "PCI-DSS",
  "UAE PDPL",
  "FSSAI",
  "MCA / Co. Law",
];
const RETAIL_REGULATORS_BY_COUNTRY = {
  india: ["DPDP", "GST Council", "Consumer Protection Act", "FSSAI", "Legal Metrology", "MCA / Co. Law", "PCI-DSS"],
  uae: ["UAE PDPL", "FTA VAT", "MOEC Consumer Protection", "DED", "PCI-DSS"],
  "united arab emirates": ["UAE PDPL", "FTA VAT", "MOEC Consumer Protection", "DED", "PCI-DSS"],
  usa: ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code", "State Sales Tax"],
  "united states": ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code", "State Sales Tax"],
  uk: ["ICO", "CMA", "HMRC VAT", "Food Standards Agency", "PCI-DSS"],
  "united kingdom": ["ICO", "CMA", "HMRC VAT", "Food Standards Agency", "PCI-DSS"],
  eu: ["GDPR", "Consumer Rights Directive", "VAT Directive", "PCI-DSS"],
  singapore: ["PDPC", "CCCS", "IRAS GST", "SFA", "PCI-DSS"],
  indonesia: ["PDP Law", "BPKN", "DGT VAT", "BPOM", "PCI-DSS"],
};
const RETAIL_AUDIT_TYPES = [
  "Store Operations & POS",
  "Inventory & Shrink",
  "Loss Prevention & Shrinkage Audit",
  "Omnichannel & E-commerce",
  "Revenue & Promotional Pricing",
  "Accounts & Financial Controls Audit",
  "Mall & Property / Lease",
  "Supply Chain & Procurement",
  "ITGC (Retail Systems)",
  "Hospitality & F&B",
  "Operational Intelligence",
  "Compliance"
];
const FINANCE_RISK_AUDIT_TYPE = "Finance & Risk";
const RETAIL_DEPARTMENTS = [
  "Store Operations",
  "Loss Prevention",
  "Finance & Accounts",
  "Merchandising & Planning",
  "Supply Chain & Logistics",
  "E-commerce & Digital",
  "Mall Management",
  "Hospitality & Leisure",
  "Health & Wellness (Retail)",
  "Corporate / Shared Services",
];

// Ã¢â€â‚¬Ã¢â€â‚¬ Telecom domain
const TELECOM_REGULATORS = ["TRAI", "DoT", "FCC", "ITU", "Ofcom", "TDRA", "TRA", "NTC", "IT Act", "DPDP"];
const TELECOM_REGULATORS_BY_COUNTRY = {
  india: ["TRAI", "DoT", "IT Act", "DPDP", "GST Council"],
  uae: ["TDRA", "TRA", "UAE PDPL"],
  "united arab emirates": ["TDRA", "TRA", "UAE PDPL"],
  usa: ["FCC", "FTC", "State Utility Commissions"],
  "united states": ["FCC", "FTC", "State Utility Commissions"],
  uk: ["Ofcom", "ICO"],
  "united kingdom": ["Ofcom", "ICO"],
  eu: ["BEREC", "GDPR", "EECC"],
  singapore: ["IMDA", "PDPC"],
  indonesia: ["SDPPI", "Kominfo", "BRTI"],
};
const TELECOM_AUDIT_TYPES = [
  "Subscriber Verification (KYC)",
  "Billing Integrity & Tariff Compliance",
  "Spectrum Usage & Licensing",
  "Network Performance & QoS",
  "Data Privacy & Cyber Security",
  "Revenue Assurance",
  "ITGC (Telecom Systems)",
  "Interconnect Billing",
  "Regulatory Compliance"
];
const TELECOM_DEPARTMENTS = [
  "Network Operations",
  "Billing & Revenue Assurance",
  "Customer Service & Activation",
  "Regulatory Affairs & Compliance",
  "Sales & Distribution",
  "Information Security & IT",
  "Finance & Accounts"
];

// ── Logistics domain (transport, freight, warehousing)
const LOGISTICS_REGULATORS = ["DVSA", "DfT", "HMRC", "HSE", "EA", "ICO", "GDPR", "FMCSA", "IATA"];
const LOGISTICS_REGULATORS_BY_COUNTRY = {
  uk: ["DVSA", "DfT", "HMRC", "HSE", "EA", "ICO", "GDPR"],
  "united kingdom": ["DVSA", "DfT", "HMRC", "HSE", "EA", "ICO", "GDPR"],
  usa: ["FMCSA", "DOT", "OSHA", "EPA", "CBP", "FTC"],
  "united states": ["FMCSA", "DOT", "OSHA", "EPA", "CBP", "FTC"],
  india: ["MoRTH", "GST Council", "DPDP", "CPCB", "DGFT"],
  uae: ["RTA", "FTA VAT", "MOIAT", "UAE PDPL"],
  "united arab emirates": ["RTA", "FTA VAT", "MOIAT", "UAE PDPL"],
  eu: ["GDPR", "ELA", "ERA", "EASA"],
  singapore: ["LTA", "MOM", "PDPC"],
  indonesia: ["Kemenhub", "PDP Law", "Bea Cukai"],
};
const LOGISTICS_AUDIT_TYPES = [
  "Fleet Operations & Maintenance",
  "Driver Compliance & Licensing",
  "Freight, Customs & Excise",
  "Warehouse & Distribution",
  "Health, Safety & Environment",
  "Fuel & Operating Cost Controls",
  "Logistics IT & Systems (ITGC)",
  "Regulatory Compliance"
];
const LOGISTICS_DEPARTMENTS = [
  "Fleet Operations",
  "Warehouse & Distribution",
  "Transport & Routing",
  "Procurement & Suppliers",
  "Finance",
  "Compliance & Safety (HSE)",
  "Customs & Trade Compliance",
  "IT & Systems"
];

const DOMAIN_REGULATOR_OPTIONS = {
  banking: { fallback: BANKING_REGULATORS, byCountry: BANKING_REGULATORS_BY_COUNTRY },
  platform: { fallback: PLATFORM_REGULATORS, byCountry: PLATFORM_REGULATORS_BY_COUNTRY },
  insurance: { fallback: INSURANCE_REGULATORS, byCountry: INSURANCE_REGULATORS_BY_COUNTRY },
  retail: { fallback: RETAIL_REGULATORS, byCountry: RETAIL_REGULATORS_BY_COUNTRY },
  telecom: { fallback: TELECOM_REGULATORS, byCountry: TELECOM_REGULATORS_BY_COUNTRY },
  logistics: { fallback: LOGISTICS_REGULATORS, byCountry: LOGISTICS_REGULATORS_BY_COUNTRY },
};

const KNOWN_REGULATORS = new Set(
  Object.values(DOMAIN_REGULATOR_OPTIONS).flatMap(({ fallback, byCountry }) => [
    ...fallback,
    ...Object.values(byCountry).flat(),
  ])
);

const normalizeCountryKey = (country) => {
  const value = String(country || "").trim().toLowerCase();
  if (!value) return "";
  if (["in", "india", "bharat"].includes(value)) return "india";
  if (["uae", "u.a.e.", "united arab emirates"].includes(value)) return "uae";
  if (["us", "usa", "u.s.", "u.s.a.", "united states", "united states of america"].includes(value)) return "usa";
  if (["uk", "u.k.", "gb", "great britain", "united kingdom"].includes(value)) return "uk";
  if (["europe", "european union", "eu", "eea"].includes(value)) return "eu";
  return value;
};

const getRegulatorsForContext = (auditDomain, country) => {
  const config = DOMAIN_REGULATOR_OPTIONS[auditDomain] || DOMAIN_REGULATOR_OPTIONS.banking;
  const key = normalizeCountryKey(country);
  return config.byCountry[key] || config.fallback;
};

const customRegulatorsFrom = (regulators) =>
  (regulators || []).filter((reg) => reg && !KNOWN_REGULATORS.has(reg));

const filterRegulatorsForContext = (auditDomain, country, regulators) => {
  const allowed = new Set(getRegulatorsForContext(auditDomain, country));
  return (regulators || []).filter((reg) => allowed.has(reg) || !KNOWN_REGULATORS.has(reg));
};

const getDomainLabel = (auditDomain) =>
  auditDomain === "platform" ? "Platform" :
  auditDomain === "insurance" ? "Insurance" :
  auditDomain === "retail" ? "Retail & consumer" :
  auditDomain === "telecom" ? "Telecom" :
  auditDomain === "logistics" ? "Logistics" :
  "Banking";

function defaultAuditPeriodIsoDates() {
  const end = new Date();
  const start = new Date(end.getTime() - 365 * 24 * 60 * 60 * 1000);
  return {
    period_start: start.toISOString().slice(0, 10),
    period_end: end.toISOString().slice(0, 10),
  };
}

function recommendAuditTypeForContext({ domain, department, riskRating }) {
  const dept = String(department || "").trim().toLowerCase();
  const risk = String(riskRating || "").trim();

  if (domain === "banking") {
    if (dept.includes("compliance") || dept.includes("aml")) return "AML/CFT";
    if (dept.includes("risk")) return "Risk-based";
    if (dept.includes("it") || dept.includes("security")) return "IT";
    return risk === "High" ? "Risk-based" : "Regulatory";
  }

  if (domain === "platform") {
    if (dept.includes("trust") || dept.includes("fraud")) return "Fraud & Trust";
    if (dept.includes("finance") || dept.includes("payout")) return "Financial & Marketplace Controls";
    if (dept.includes("security") || dept.includes("it")) return "Technology Governance";
    return "Operational Platform Controls";
  }

  if (domain === "insurance") {
    if (dept.includes("underwriting")) return "Underwriting Audit";
    if (dept.includes("it")) return "IT Governance";
    return "Claims Audit";
  }

  if (domain === "telecom") {
    if (dept.includes("billing") || dept.includes("revenue")) return "Billing Integrity & Tariff Compliance";
    if (dept.includes("network") || dept.includes("qos")) return "Network Performance & QoS";
    if (dept.includes("security") || dept.includes("it")) return "Data Privacy & Cyber Security";
    if (dept.includes("regulatory") || dept.includes("compliance")) return "Regulatory Compliance";
    return "Subscriber Verification (KYC)";
  }

  if (domain === "logistics") {
    if (dept.includes("fleet") || dept.includes("driver")) return "Fleet Operations & Maintenance";
    if (dept.includes("customs") || dept.includes("trade")) return "Freight, Customs & Excise";
    if (dept.includes("safety") || dept.includes("hse")) return "Health, Safety & Environment";
    if (dept.includes("warehouse") || dept.includes("distribution")) return "Warehouse & Distribution";
    if (dept.includes("it") || dept.includes("system")) return "Logistics IT & Systems (ITGC)";
    if (dept.includes("finance") || dept.includes("fuel") || dept.includes("cost")) return "Fuel & Operating Cost Controls";
    return "Regulatory Compliance";
  }

  if (dept.includes("loss prevention") || dept.includes("shrink")) return "Loss Prevention & Shrinkage Audit";
  if (dept.includes("finance") || dept.includes("accounts")) return "Accounts & Financial Controls Audit";
  if (dept.includes("e-commerce") || dept.includes("digital")) return "Omnichannel & E-commerce";
  if (dept.includes("supply chain") || dept.includes("logistics")) return "Supply Chain & Procurement";
  if (dept.includes("mall")) return "Mall & Property / Lease";
  return risk === "High" ? "Operational Intelligence" : "Store Operations & POS";
}

/** FastAPI validation errors expose `detail` as a string, object, or array of { loc, msg, type }. */
function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.msg) return item.msg;
        if (item?.loc && item?.msg) return `${item.loc.filter(Boolean).join(".")}: ${item.msg}`;
        try {
          return JSON.stringify(item);
        } catch {
          return String(item);
        }
      })
      .filter(Boolean)
      .join("; ");
  }
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return null;
}

export default function Phase1Context() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, refreshRun, setNewAuditDialogOpen } = useAudit();
  const [saving, setSaving] = useState(false);
  const [customRegulator, setCustomRegulator] = useState("");
  const [customDepartment, setCustomDepartment] = useState("");
  const [domain, setDomain] = useState("banking"); // "banking" | "platform" | "insurance" | "retail" | "telecom" | "logistics"
  const defaultForm = {
    country: "", regulators: [], audit_type: "", department: "",
    risk_rating: "Medium", period_start: "", period_end: "", depth_level: "Standard"
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (!auditRun?.context) return;
    const ad = auditRun.context.audit_domain;
    const resolvedDomain = (ad === "banking" || ad === "platform" || ad === "insurance" || ad === "retail" || ad === "telecom" || ad === "logistics") ? ad : "banking";
    setForm(prev => ({
      ...defaultForm,
      ...auditRun.context,
      regulators: filterRegulatorsForContext(
        resolvedDomain,
        auditRun.context.country,
        auditRun.context.regulators
      ),
    }));
    if (ad === "banking" || ad === "platform" || ad === "insurance" || ad === "retail" || ad === "telecom" || ad === "logistics") {
      setDomain(ad);
    }
  }, [auditRun?.context]);

  const REGULATORS = getRegulatorsForContext(domain, form.country);

  const AUDIT_TYPES =
    domain === "platform" ? PLATFORM_AUDIT_TYPES :
    domain === "insurance" ? INSURANCE_AUDIT_TYPES :
    domain === "retail" ? RETAIL_AUDIT_TYPES :
    domain === "telecom" ? TELECOM_AUDIT_TYPES :
    domain === "logistics" ? LOGISTICS_AUDIT_TYPES :
    BANKING_AUDIT_TYPES;
  const AUDIT_TYPES_WITH_FINANCE_RISK = Array.from(new Set([...AUDIT_TYPES, FINANCE_RISK_AUDIT_TYPE]));

  const DEPARTMENTS =
    domain === "platform" ? PLATFORM_DEPARTMENTS :
    domain === "insurance" ? INSURANCE_DEPARTMENTS :
    domain === "retail" ? RETAIL_DEPARTMENTS :
    domain === "telecom" ? TELECOM_DEPARTMENTS :
    domain === "logistics" ? LOGISTICS_DEPARTMENTS :
    BANKING_DEPARTMENTS;

  const ALL_REGULATORS = Array.from(
    new Set([...(REGULATORS || []), ...customRegulatorsFrom(form.regulators)])
  );

  const suggestedRegulatorsFor = (newDomain, country, currentRegulators = []) =>
    Array.from(new Set([
      ...getRegulatorsForContext(newDomain, country),
      ...customRegulatorsFrom(currentRegulators),
    ]));

  const handleDomainSwitch = (newDomain) => {
    setDomain(newDomain);
    setForm(prev => ({
      ...prev,
      audit_type: "",
      regulators: suggestedRegulatorsFor(newDomain, prev.country, prev.regulators),
    }));
  };

  const handleCountryChange = (value) => {
    setForm(prev => ({
      ...prev,
      country: value,
      regulators: suggestedRegulatorsFor(domain, value, prev.regulators),
    }));
  };

  const toggleRegulator = (reg) => {
    setForm(prev => ({
      ...prev,
      regulators: prev.regulators.includes(reg)
        ? prev.regulators.filter(r => r !== reg)
        : [...prev.regulators, reg]
    }));
  };

  const addCustomRegulator = () => {
    const value = (customRegulator || "").trim();
    if (!value) return;
    setForm((prev) => ({
      ...prev,
      regulators: prev.regulators.includes(value) ? prev.regulators : [...prev.regulators, value],
    }));
    setCustomRegulator("");
  };

  const addCustomDepartment = () => {
    const value = (customDepartment || "").trim();
    if (!value) return;
    setForm((prev) => ({ ...prev, department: value }));
    setCustomDepartment("");
  };


  const handleSave = async ({ advance = true } = {}) => {
    if (!auditRun) { toast.error("Create an audit run first"); return; }

    let period_start = (form.period_start || "").trim();
    let period_end = (form.period_end || "").trim();

    if (!period_start && !period_end) {
      const d = defaultAuditPeriodIsoDates();
      period_start = d.period_start;
      period_end = d.period_end;
      setForm((p) => ({ ...p, period_start, period_end }));
      toast.info("Audit period was empty Ã¢â‚¬â€ defaulted to the last 12 months. Adjust the dates if needed.");
    } else if (!period_start || !period_end) {
      toast.error("Set both period start and end, or clear both to use a default 12Ã¢â‚¬â€˜month range.");
      return;
    }

    if (period_end < period_start) {
      toast.error("Period end must be on or after period start.");
      return;
    }

    if (!(form.country || "").trim()) {
      toast.error("Country / jurisdiction is required.");
      return;
    }
    if (!(form.department || "").trim()) {
      toast.error("Department under audit is required.");
      return;
    }
    if (!(form.audit_type || "").trim()) {
      toast.error("Audit type is required.");
      return;
    }
    const effectiveRegulators = filterRegulatorsForContext(domain, form.country, form.regulators);
    if (!Array.isArray(effectiveRegulators) || effectiveRegulators.length === 0) {
      toast.error("Select at least one regulator or framework.");
      return;
    }
    
    if (!(form.depth_level || "").trim()) {
      toast.error("Depth level is required.");
      return;
    }

    const contextData = {
      ...form,
      country: (form.country || "").trim(),
      department: (form.department || "").trim(),
      regulators: filterRegulatorsForContext(domain, form.country, form.regulators),
      period_start,
      period_end,
      audit_domain: domain,
    };
    
    const payload = { context: contextData };

    setSaving(true);
    try {
      await API.put(`/audit-runs/${auditRun.id}/context`, payload);

      // Finance & Risk external analytics redirect disabled — proceed with the normal phase flow.
      // if ((contextData.audit_type || "").trim().toLowerCase() === FINANCE_RISK_AUDIT_TYPE.toLowerCase()) {
      //   toast.success("Context saved. Redirecting to Finance & Risk Analytics...");
      //   window.location.href = "https://analytics.zentis.ai/finance";
      //   return;
      // }

      await refreshRun();
      if (advance) {
        await advancePhase(2);
        toast.success("Context saved. Proceeding to Document Ingestion.");
        navigate("/phase/2");
      } else {
        toast.success("Context saved as draft.");
      }
    } catch (e) {
      const apiMsg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(apiMsg || e.message || "Failed to save context");
    }
    setSaving(false);
  };



  const depthRecommendation = form.risk_rating === "High" ? "Comprehensive" : form.risk_rating === "Medium" ? "Standard" : "Limited";
  const phaseInputClass = "phase1-input";
  const phaseSelectClass = "phase1-select";
  const domainButtonClass = (active) => `phase1-domain-btn ${active ? "is-active" : ""}`;
  const chipButtonClass = (active) => `phase1-chip ${active ? "is-active" : ""}`;
  const primaryButtonClass = "phase1-primary-btn";
  const domainTiles = [
    { id: "banking", label: "Banking & Financial", icon: Building2 },
    { id: "retail", label: "Retail & Consumer", icon: Store },
    { id: "platform", label: "Platform & E-Commerce", icon: ShoppingBag },
    { id: "insurance", label: "Insurance", icon: ShieldCheck },
    { id: "telecom", label: "Telecom", icon: Network },
    { id: "logistics", label: "Logistics", icon: Truck },
  ];
  const countryOptions = ["US", "India", "UAE", "UK", "EU", "Singapore"];

  return (
    <PageLayout
      icon={FileText}
      title="Phase 1: Content Setup"
      subtitle="Define the audit context, jurisdiction, and parameters to personalize the audit experience."
      className="space-y-6 max-w-7xl mx-auto pb-12"
      data-testid="phase-1"
      actions={
        auditRun ? (
          <div className="flex items-center gap-3">
            {auditRun?.name && (
              <Badge variant="outline" className="hidden sm:inline-flex bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-xs font-semibold px-3 py-1">
                {auditRun.name}
              </Badge>
            )}
            <Button
              type="button"
              onClick={() => handleSave({ advance: true })}
              disabled={saving}
              data-testid="save-context-btn"
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-5 shadow-md shadow-emerald-600/30 border-0 transition-all active:scale-95"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
              Save &amp; Continue
            </Button>
          </div>
        ) : null
      }
    >

      {!auditRun && (
        <Card
          className="rounded-3xl border-2 border-dashed border-border/80 p-12 text-center hover:border-emerald-500/40 transition-colors cursor-pointer"
          onClick={() => setNewAuditDialogOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setNewAuditDialogOpen(true);
            }
          }}
        >
          <CardContent className="space-y-4 p-0">
            <div className="h-16 w-16 mx-auto rounded-3xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <FileText className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Create a new audit run</h2>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                Start an audit to unlock the content setup workflow and initialize risk controls.
              </p>
            </div>
            <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-11 px-6 shadow-md shadow-emerald-600/30 border-0" data-testid="phase-1-new-audit-btn">
              New Audit
            </Button>
          </CardContent>
        </Card>
      )}

      {auditRun && (
        <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden bg-card">
          <CardHeader className="bg-muted/40 py-4 px-6 sm:px-8 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground">Audit Configuration Parameters</CardTitle>
          </CardHeader>

          <CardContent className="p-6 sm:p-8 space-y-6">
            {/* DOMAIN SELECTOR */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-foreground">Audit Domain Vertical</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {domainTiles.map((tile) => {
                  const Icon = tile.icon;
                  const active = domain === tile.id;
                  return (
                    <button
                      key={tile.id}
                      type="button"
                      onClick={() => handleDomainSwitch(tile.id)}
                      data-testid={`domain-${tile.id}`}
                      className={`flex flex-col items-center justify-center p-3.5 rounded-2xl border text-center transition-all ${
                        active
                          ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-700 dark:text-emerald-300 font-bold shadow-sm"
                          : "border-border/80 bg-card text-muted-foreground hover:border-border hover:bg-muted/30"
                      }`}
                    >
                      <Icon className="h-5 w-5 mb-1.5 shrink-0" />
                      <span className="text-xs">{tile.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* DOMAIN INFO BANNER */}
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-xs font-bold shrink-0">
                i
              </div>
              <p className="text-xs text-emerald-900 dark:text-emerald-200 leading-relaxed">
                <strong>{getDomainLabel(domain)} Mode:</strong> Frameworks, regulatory bodies, audit types, and department structures are tailored for {getDomainLabel(domain).toLowerCase()} audits.
              </p>
            </div>

            {/* COUNTRY & DEPARTMENT */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Country / Jurisdiction</Label>
                <div className="relative">
                  <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    value={form.country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    placeholder="US, India, UAE..."
                    data-testid="input-country"
                    className="h-11 pl-10 pr-4 rounded-xl text-xs bg-card border-border/80"
                    list="phase1-country-options"
                  />
                </div>
                <datalist id="phase1-country-options">
                  {countryOptions.map((country) => (
                    <option key={country} value={country} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Department Under Audit</Label>
                <div className="relative">
                  <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    value={form.department}
                    onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
                    placeholder="Billing, Operations, Risk..."
                    data-testid="input-department"
                    className="h-11 pl-10 pr-4 rounded-xl text-xs bg-card border-border/80"
                  />
                </div>

                <div className="flex flex-wrap gap-1.5 pt-1">
                  {DEPARTMENTS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, department: d }))}
                      className={`text-[11px] font-semibold px-3 py-1 rounded-xl border transition-colors ${
                        form.department === d
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                          : "border-border/80 bg-muted/40 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 pt-1">
                  <Input
                    value={customDepartment}
                    onChange={(e) => setCustomDepartment(e.target.value)}
                    placeholder="Other custom department..."
                    className="h-9 text-xs rounded-xl bg-card border-border/80"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCustomDepartment();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" className="h-9 px-4 rounded-xl text-xs font-semibold" onClick={addCustomDepartment}>
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* REGULATORS */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-foreground">Regulators &amp; Governance Frameworks (Multi-Select)</Label>
              <div className="flex flex-wrap gap-2 p-3 rounded-2xl border border-border/80 bg-muted/20" data-testid="regulator-select">
                {ALL_REGULATORS.map((r) => (
                  <Badge
                    key={r}
                    variant="outline"
                    className={`text-xs font-semibold px-3 py-1 rounded-xl cursor-pointer transition-all ${
                      form.regulators.includes(r)
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-card border-border/80 text-muted-foreground hover:bg-muted"
                    }`}
                    onClick={() => toggleRegulator(r)}
                  >
                    {r}
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2 max-w-md">
                <Input
                  value={customRegulator}
                  onChange={(e) => setCustomRegulator(e.target.value)}
                  placeholder="Custom regulator or framework..."
                  className="h-9 text-xs rounded-xl bg-card border-border/80"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomRegulator();
                    }
                  }}
                />
                <Button type="button" variant="outline" className="h-9 px-4 rounded-xl text-xs font-semibold" onClick={addCustomRegulator}>
                  Add
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Showing regulators matched to {form.country || "the selected jurisdiction"} and {getDomainLabel(domain).toLowerCase()} audits.
              </p>
            </div>

            {/* AUDIT TYPE & RISK RATING */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Audit Type</Label>
                <Select value={form.audit_type} onValueChange={(v) => setForm((p) => ({ ...p, audit_type: v }))}>
                  <SelectTrigger className="h-11 rounded-xl bg-card border-border/80 text-xs font-medium" data-testid="select-audit-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {AUDIT_TYPES_WITH_FINANCE_RISK.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Initial Risk Rating</Label>
                <Select value={form.risk_rating} onValueChange={(v) => setForm((p) => ({ ...p, risk_rating: v, depth_level: v === "High" ? "Comprehensive" : v === "Medium" ? "Standard" : "Limited" }))}>
                  <SelectTrigger className="h-11 rounded-xl bg-card border-border/80 text-xs font-medium" data-testid="select-risk-rating">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="High" className="text-xs font-semibold text-red-600">High Risk</SelectItem>
                    <SelectItem value="Medium" className="text-xs font-semibold text-amber-600">Medium Risk</SelectItem>
                    <SelectItem value="Low" className="text-xs font-semibold text-emerald-600">Low Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* PERIOD & DEPTH */}
            <div className="grid gap-6 sm:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Period Start</Label>
                <Input
                  type="date"
                  value={form.period_start}
                  onChange={(e) => setForm((p) => ({ ...p, period_start: e.target.value }))}
                  data-testid="input-period-start"
                  className="h-11 rounded-xl bg-card border-border/80 font-mono text-xs"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Period End</Label>
                <Input
                  type="date"
                  value={form.period_end}
                  onChange={(e) => setForm((p) => ({ ...p, period_end: e.target.value }))}
                  data-testid="input-period-end"
                  className="h-11 rounded-xl bg-card border-border/80 font-mono text-xs"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-foreground">Audit Depth Level</Label>
                <Select value={form.depth_level} onValueChange={(v) => setForm((p) => ({ ...p, depth_level: v }))}>
                  <SelectTrigger className="h-11 rounded-xl bg-card border-border/80 text-xs font-medium" data-testid="select-depth">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="Limited" className="text-xs">Limited Scope</SelectItem>
                    <SelectItem value="Standard" className="text-xs">Standard Scope</SelectItem>
                    <SelectItem value="Comprehensive" className="text-xs">Comprehensive Scope</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Recommendation: <span className="font-bold text-emerald-600 dark:text-emerald-400">{depthRecommendation}</span> based on {form.risk_rating} rating.
                </p>
              </div>
            </div>

            {/* BOTTOM ACTIONS BAR */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-border/60">
              <span className="text-xs text-muted-foreground font-medium">
                Save draft or proceed directly to Phase 2 Document Ingestion.
              </span>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl text-xs font-semibold h-11 px-5"
                  onClick={() => handleSave({ advance: false })}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                <Button
                  type="button"
                  onClick={() => handleSave({ advance: true })}
                  disabled={saving}
                  data-testid="save-context-btn"
                  className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-11 px-6 shadow-md shadow-emerald-600/30 border-0 active:scale-95 transition-all"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Save &amp; Continue
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </PageLayout>
  );
}
//               <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-500">
//                 Fill in every Phase 1 field before moving to Document Ingestion.
//               </div>
//             )}

//             <div>
//               <h3 className="text-sm font-medium mb-1 flex items-center gap-2">
//                 <ShoppingBag className="w-4 h-4 text-primary" /> Platform &amp; E-Commerce Mode
//               </h3>
//               <p className="text-xs text-amber-600 dark:text-amber-400">
//                 Ã¢Å¡Â¡ FSSAI, GST, DPDP regulators pre-loaded. Suitable for internal audits of platform operations.
//               </p>
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-xs">Country / Jurisdiction</Label>
//                 <Input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))}
//                   placeholder="e.g., India" data-testid="input-country" className={fieldErrorClass(validationErrors.country)} />
//                 {renderFieldError(attemptedSave && validationErrors.country, "Country / jurisdiction is required.")}
//               </div>
//               <div>
//                 <Label className="text-xs">Department Under Audit</Label>
//                 <Input value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
//                   placeholder="e.g., Delivery Fleet Operations"
//                   data-testid="input-department" className={fieldErrorClass(validationErrors.department)} />
//                 {renderFieldError(attemptedSave && validationErrors.department, "Department under audit is required.")}
//                 <div className="flex flex-wrap gap-1 mt-1.5">
//                   {PLATFORM_DEPARTMENTS.map(d => (
//                     <button key={d} type="button"
//                       onClick={() => setForm(p => ({ ...p, department: d }))}
//                       className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${form.department === d
//                           ? "bg-primary text-primary-foreground border-primary"
//                           : "border-border text-muted-foreground hover:border-primary/50"
//                         }`}
//                     >{d}</button>
//                   ))}
//                 </div>
//               </div>
//             </div>

//             <div>
//               <Label className="text-xs">
//                 Regulators / Frameworks (multi-select)
//                 <span className="ml-2 text-muted-foreground font-normal">
//                   Ã¢â‚¬â€ Platform frameworks
//                 </span>
//               </Label>
//               <div className={`flex flex-wrap gap-1.5 mt-1 rounded-md ${attemptedSave && validationErrors.regulators ? "ring-1 ring-red-500/60 p-2" : ""}`} data-testid="regulator-select">
//                 {REGULATORS.map(r => (
//                   <Badge key={r}
//                     variant={form.regulators.includes(r) ? "default" : "outline"}
//                     className="cursor-pointer text-xs"
//                     onClick={() => toggleRegulator(r)}
//                   >
//                     {r}
//                   </Badge>
//                 ))}
//               </div>
//               {renderFieldError(attemptedSave && validationErrors.regulators, "Select at least one regulator or framework.")}
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-xs">Audit Type</Label>
//                 <Select value={form.audit_type} onValueChange={v => setForm(p => ({ ...p, audit_type: v }))}>
//                   <SelectTrigger className={fieldErrorClass(validationErrors.audit_type)} data-testid="select-audit-type"><SelectValue placeholder="Select type" /></SelectTrigger>
//                   <SelectContent>
//                     {AUDIT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
//                   </SelectContent>
//                 </Select>
//                 {renderFieldError(attemptedSave && validationErrors.audit_type, "Audit type is required.")}
//               </div>
//               <div>
//                 <Label className="text-xs">Risk Rating</Label>
//                 <Select value={form.risk_rating} onValueChange={v => setForm(p => ({ ...p, risk_rating: v, depth_level: v === "High" ? "Comprehensive" : v === "Medium" ? "Standard" : "Limited" }))}>
//                   <SelectTrigger className={fieldErrorClass(validationErrors.risk_rating)} data-testid="select-risk-rating"><SelectValue /></SelectTrigger>
//                   <SelectContent>
//                     <SelectItem value="High">High</SelectItem>
//                     <SelectItem value="Medium">Medium</SelectItem>
//                     <SelectItem value="Low">Low</SelectItem>
//                   </SelectContent>
//                 </Select>
//                 {renderFieldError(attemptedSave && validationErrors.risk_rating, "Risk rating is required.")}
//               </div>
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-xs">Period Start</Label>
//                 <Input type="date" value={form.period_start} onChange={e => setForm(p => ({ ...p, period_start: e.target.value }))}
//                   data-testid="input-period-start" className={`${fieldErrorClass(validationErrors.period_start || validationErrors.period_order)} font-mono`} />
//                 {renderFieldError(attemptedSave && validationErrors.period_start, "Period start is required.")}
//               </div>
//               <div>
//                 <Label className="text-xs">Period End</Label>
//                 <Input type="date" value={form.period_end} onChange={e => setForm(p => ({ ...p, period_end: e.target.value }))}
//                   data-testid="input-period-end" className={`${fieldErrorClass(validationErrors.period_end || validationErrors.period_order)} font-mono`} />
//                 {renderFieldError(attemptedSave && validationErrors.period_end, "Period end is required.")}
//                 {renderFieldError(attemptedSave && validationErrors.period_order, "Period end must be on or after period start.")}
//               </div>
//             </div>

//             <div>
//               <Label className="text-xs">Depth Level</Label>
//               <Select value={form.depth_level} onValueChange={v => setForm(p => ({ ...p, depth_level: v }))}>
//                 <SelectTrigger className={fieldErrorClass(validationErrors.depth_level)} data-testid="select-depth"><SelectValue /></SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="Limited">Limited</SelectItem>
//                   <SelectItem value="Standard">Standard</SelectItem>
//                   <SelectItem value="Comprehensive">Comprehensive</SelectItem>
//                 </SelectContent>
//               </Select>
//               {renderFieldError(attemptedSave && validationErrors.depth_level, "Depth level is required.")}
//               <p className="text-xs text-muted-foreground mt-1">
//                 Recommendation: <span className="text-emerald-600 dark:text-emerald-400 font-medium">{depthRecommendation}</span> based on {form.risk_rating} risk rating.
//               </p>
//             </div>

//             <div className="flex justify-end gap-2 pt-2">
//               <Button variant="outline" onClick={handleRecommendAutofill} disabled={recommending || saving} data-testid="recommend-autofill-btn">
//                 {recommending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
//                 Recommend &amp; Autofill
//               </Button>
//               <Button onClick={handleSave} disabled={saving} data-testid="save-context-btn">
//                 {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
//                 Save &amp; Continue
//               </Button>
//             </div>
//           </CardContent>
//         </Card>
//       )}
//     </div>
//   );
// }
